import styled, { css, keyframes } from 'styled-components';

/* Visual tokens — ported from the PandaDocAnalytics page so the Finances
 * section feels like one product. Kept local to this folder to avoid
 * coupling across pages — duplication is cheap and stable. */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: -1px;
  flex-shrink: 0;
`;

export const Wrapper = styled.div`
  width: 100%;
  margin: 32px 0 64px;
  color: #0f172a;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

export const BackLink = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

export const SubTitle = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-bottom: 16px;
`;

export const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
  margin: 16px 0 4px;
`;

export const TabBarItem = styled.button<{ active: boolean }>`
  border: 0;
  background: transparent;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) => (active ? '#0f172a' : '#64748b')};
  border-bottom: 2px solid
    ${({ active }) => (active ? '#0f172a' : 'transparent')};
  margin-bottom: -1px;
  transition: color 0.15s;

  &:hover {
    color: #0f172a;
  }
`;

export const HeaderRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const UnmatchedBadge = styled.button`
  border: 0;
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #ffedd5;
  }
`;

export const FilterBar = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin: 16px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const FilterLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
`;

export const SegmentTabs = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
`;

export const SegmentTab = styled.button<{ active: boolean }>`
  border: 0;
  background: ${({ active }) => (active ? '#fff' : 'transparent')};
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ active }) => (active ? '#0f172a' : '#64748b')};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s;
  box-shadow: ${({ active }) =>
    active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'};
`;

export const NativeSelect = styled.select`
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  background: #fff;
  outline: none;

  &:focus {
    border-color: #2563eb;
  }
`;

export const NativeInput = styled.input`
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  background: #fff;
  outline: none;
  min-width: 220px;

  &:focus {
    border-color: #2563eb;
  }
`;

export const ResetButton = styled.button`
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  text-decoration: underline;

  &:hover {
    color: #0f172a;
  }
`;

export const Banner = styled.div<{ variant?: 'warn' | 'success' | 'info' }>`
  border-radius: 10px;
  padding: 12px 16px;
  margin: 8px 0 16px;
  font-size: 13px;
  line-height: 1.5;
  ${({ variant }) => {
    if (variant === 'success') {
      return css`
        border: 1px solid #bbf7d0;
        background: #f0fdf4;
        color: #166534;
      `;
    }
    if (variant === 'info') {
      // Phase 12: «part of order» banner uses a blue palette to read as
      // informational rather than an alert — sibling to «correction of».
      return css`
        border: 1px solid #bfdbfe;
        background: #eff6ff;
        color: #1e40af;
      `;
    }
    return css`
      border: 1px solid #fed7aa;
      background: #fff7ed;
      color: #9a3412;
    `;
  }}
`;

export const BannerTitle = styled.div`
  font-weight: 700;
  margin-bottom: 2px;
`;

/** Inline retry inside a warn banner (drain error surfacing — audit). */
export const BannerRetry = styled.button`
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  border-radius: 6px;
  padding: 3px 10px;
  margin-top: 4px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;

  thead th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 600;
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
  }

  tbody td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.1s;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

export const NameCell = styled.td`
  font-weight: 600;
  color: #0f172a;
`;

export const TagCell = styled.td`
  white-space: nowrap;
`;

export const RowActionsCell = styled.td`
  text-align: right;
  white-space: nowrap;
`;

export const InlineButton = styled.button`
  border: 1px solid #2563eb;
  background: #fff;
  color: #2563eb;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #2563eb;
    color: #fff;
  }
`;

export const EmptyState = styled.div`
  padding: 56px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
`;

export const EmptyHint = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
`;

export const CenterLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;

  & > div {
    width: 48px;
    height: 48px;
  }
`;

export const DimWhenFetching = styled.div<{ fetching: boolean }>`
  transition: opacity 0.15s;
  ${({ fetching }) =>
    fetching &&
    css`
      opacity: 0.55;
      pointer-events: none;
    `}
`;

/* ============= Detail page tokens ============= */

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 12px;
`;

export const Row = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  font-size: 13px;
  align-items: center;
`;

export const RowLabel = styled.div`
  color: #64748b;
  flex: 0 0 160px;
`;

export const RowValue = styled.div`
  color: #0f172a;
  font-weight: 500;
  flex: 1;
`;

export const HeadingPills = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin: 4px 0 12px;
`;

export const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ActionButton = styled.button<{
  tone?: 'primary' | 'danger' | 'ghost';
}>`
  border: 1px solid
    ${({ tone }) =>
      tone === 'danger'
        ? '#fecaca'
        : tone === 'primary'
        ? '#2563eb'
        : '#e2e8f0'};
  background: ${({ tone }) => (tone === 'primary' ? '#2563eb' : '#fff')};
  color: ${({ tone }) =>
    tone === 'primary' ? '#fff' : tone === 'danger' ? '#991b1b' : '#0f172a'};
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${({ tone }) =>
      tone === 'primary'
        ? '#1d4ed8'
        : tone === 'danger'
        ? '#fef2f2'
        : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const RawPayloadToggle = styled.button`
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const RawPre = styled.pre`
  font-family: ui-monospace, monospace;
  font-size: 11px;
  background: #0f172a;
  color: #e2e8f0;
  padding: 12px;
  border-radius: 8px;
  overflow: auto;
  max-height: 320px;
`;
